import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import type { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { CopyToClipboard } from '@openx/components-core/lib/CopyToClipboard/CopyToClipboard.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Group } from '../../utils/context/DealHookTypes.ts';
import { MAX_DEAL_GROUP_SIZE } from './constants.ts';
import { DealGroupListItem } from './DealGroupListItem.tsx';
import { sortDealGroup } from './utils/sortDealGroup.ts';

type DealGroupContainerProps = {
  group: Group;
  dealId: string;
  dealUid?: string;
  loading?: boolean;
  details?: boolean;
  isEdit?: boolean;
};

const listStyles: SxProps = {
  '&': {
    scrollbarGutter: 'stable',
  },
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'background.boxDarkest',
    borderRadius: 4,
  },
  '&:last-child': {
    marginBottom: 0,
  },
  borderLeft: 'none',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 2,
  maxHeight: MAX_DEAL_GROUP_SIZE * 28,
  overflowY: 'auto',
  paddingLeft: 0,
  paddingRight: 1,
  width: '100%',
};

export const DealGroupContainer = ({
  group,
  dealId,
  dealUid,
  loading = false,
  details = false,
  isEdit = false,
}: DealGroupContainerProps) => {
  const { t } = useTranslation();
  const sortedGroup = useMemo(() => sortDealGroup(group, dealUid), [group, dealUid]);
  const groupLengthText = !loading && ` [ ${group.length} ]`;
  const shouldRenderList = !loading && sortedGroup.length > 0;
  const shouldRenderEmptyMessage = !loading && sortedGroup.length === 0;

  return (
    <Box
      sx={theme => ({
        border: 1,
        borderColor: theme.palette.divider,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginTop: !details ? theme.spacing(1) : 0,
        maxWidth: '672px',
        padding: theme.spacing(2, 3),
      })}
      data-test="deal-group-container"
    >
      <Box>
        <Typography
          variant="body1"
          data-test="deal-group-title"
          sx={{
            fontWeight: 'fontWeightBold',
            fontSize: '16px',
          }}
        >
          {t('Grouped Deal ID')}
        </Typography>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            width: '100%',
          }}
        >
          <Typography
            variant="body1"
            data-test="grouped-deal-id-value"
            sx={{
              fontWeight: 'fontWeightBold',
              fontSize: '12px',
            }}
          >
            {dealId}
            {groupLengthText}
          </Typography>

          {loading && <Loader size={12} noPadding data-test="deal-group-loader" />}

          {(details || isEdit) && (
            <CopyToClipboard tooltipText={t('Copy External Deal ID to clipboard')} text={dealId} />
          )}
        </Box>
      </Box>

      {!loading && <Divider data-test="deal-group-divider" />}

      {shouldRenderList && (
        <Box sx={listStyles} data-test="grouped-deals-list">
          {sortedGroup.map(props => (
            <DealGroupListItem {...props} details={details} key={props.uid} />
          ))}
        </Box>
      )}

      {shouldRenderEmptyMessage && (
        <Typography variant="body1" color="textSecondary" sx={{ fontSize: '12px' }} data-test="deal-group-empty">
          {t('There are no deals in this group yet.')}
        </Typography>
      )}
    </Box>
  );
};
